import { useEffect, useState } from "react";

import {
    Button,
    CircularProgress,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import { createExportFile } from "../../../../api/exportAPI";
import { getUserFile } from "../../../../api/getFileAPI";
import { BASE_IMG_URL } from "../../../../constants/mainAPI";
import { UploadButtonForm } from "./UploadButtonForm";
import { Controller, useFormContext } from "react-hook-form";
import { ReportData } from "../../../../types/api/report";
import { DocumentData } from "../../../../types/api/documents";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { ReportTypes } from "../../../../constants/reportTypes";
import { useSearchParams } from "react-router-dom";
import { Form1EditForm } from "./Form1EditForm";
import { Form3EditForm } from "./Form3EditForm";
import { Form4EditForm } from "./Form4EditForm";
import { Form5EditForm } from "./Form5EditForm";
import { Form6EditForm } from "./Form6EditForm";

interface IProps {
    type: ReportTypes;
    centerId: number;
    reportId: number;
    editForm?: boolean;
}

function downloadFile(url: string) {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
}

const renderComponent = (type: ReportTypes, editForm?: boolean) => {
    switch (type) {
        case ReportTypes.Form_1_2: {
            return <Form1EditForm editForm={editForm} />;
        }
        case ReportTypes.Form_3: {
            return <Form3EditForm editForm={editForm} />;
        }
        case ReportTypes.Form_4: {
            return <Form4EditForm editForm={editForm} />;
        }
        case ReportTypes.Form_5: {
            return <Form5EditForm editForm={editForm} />;
        }
        case ReportTypes.Form_6: {
            return <Form6EditForm editForm={editForm} />;
        }
        default:
            return <></>;
    }
};

export const ReportSelector = ({ type, editForm, centerId, reportId }: IProps) => {
    const isOwner =
        localStorage.getItem("employee_type") == "16" &&
        localStorage.getItem("employee_center_id") == localStorage.getItem("current_center_id");
    const [search] = useSearchParams();
    const reportType = Number(search.get("report_type"));
    const reportDateId = Number(search.get("report_date_id"));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<any>(null);
    const [signFiles, setSignFiles] = useState<DocumentData[] | null>();
    const { control, getValues } = useFormContext<Pick<ReportData, "document_id" | "document">>();

    const handleClick = async () => {
        setLoading(true);
        setError(null);

        try {
            // Первый запрос
            await createExportFile(
                { type: reportType, ids: [centerId], report_id: reportId, report_date_id: reportDateId },
                () => {},
                err => {
                    setError(err);
                },
            );

            // Второй запрос
            await getUserFile(
                2,
                data => {
                    const lastFile = data.data.shift();
                    downloadFile(`${BASE_IMG_URL}/${lastFile?.path}`);
                },
                err => {
                    setError(err);
                },
            );
        } catch (err) {
            setError("Ошибка при выполнении запросов");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setSignFiles(getValues("document"));
    }, []);

    return (
        <>
            <Stack justifyContent={"flex-start"}>
                <Button
                    disabled={loading}
                    variant="outlined"
                    onClick={handleClick}
                    startIcon={<>{loading && <CircularProgress size={"20px"} />}</>}>
                    Скачать форму
                </Button>
            </Stack>
            {renderComponent(type, editForm)}
            <Stack justifyContent={"flex-start"} direction={"column"} gap={2}>
                {isOwner && (
                    <Controller
                        name={"document_id"}
                        control={control}
                        rules={{
                            maxLength: {
                                value: 120,
                                message: "Максимальная длина 120 символов",
                            },
                        }}
                        render={({ field: { onChange, value, ...field } }) => (
                            <UploadButtonForm
                                onChange={val => {
                                    onChange(val);
                                }}
                                buttonText="Загрузить подписанный документ"
                                getDocId
                            />
                        )}
                    />
                )}
                {signFiles && (
                    <Table
                        aria-label="report table"
                        sx={{
                            "& td": { border: "none" },
                        }}>
                        <TableHead sx={{ "& th": { fontSize: "16px", fontWeight: 700 } }}>
                            <TableRow>
                                <TableCell>Название</TableCell>
                                <TableCell align="center">Дата формирования</TableCell>
                                <TableCell align="center">Скачать</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {signFiles?.map(({ id, name, path, created_at }) => (
                                <TableRow key={id} sx={{ border: 0 }}>
                                    <TableCell sx={{ wordWrap: "break-word" }}>{name}</TableCell>
                                    <TableCell align="center">
                                        {format(new Date(created_at), "dd.MM.yyyy HH:mm", { locale: ru })}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            aria-label="download-icon"
                                            size="small"
                                            href={`${BASE_IMG_URL}/${path}`}>
                                            <FileDownloadIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Stack>
        </>
    );
};
